<script setup lang="ts">
import { INotificationPayment } from '@shared/types'
import { formatPrice } from '@/components/utils/functions'
import { formatDate } from '@/utils/dateFormatter'
import { computed, ref } from 'vue'
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage'
const props = defineProps<{
	pago: INotificationPayment
}>()

const pago = computed(() => props.pago)
const storage = getStorage()
const institutionInitial = computed(() => (pago.value.institutionName?.trim()?.[0] ?? '?').toUpperCase())
const proofDataUrl = ref<string>('')
const paymentDateText = computed(() => formatDate(pago.value.dateToSendNotification))
const getLastNonEmptySegment = (value: string) => {
	const parts = value.split('/')
	for (let i = parts.length - 1; i >= 0; i--) {
		const segment = parts[i]
		if (segment) return segment
	}
	return ''
}
const proofDisplayText = computed(() => {
	const storagePath = pago.value.proof?.trim()
	if (storagePath) {
		const last = getLastNonEmptySegment(storagePath)
		return last ? decodeURIComponent(last) : storagePath
	}

	const url = proofDataUrl.value?.trim()
	if (!url) return ''
	try {
		const parsed = new URL(url)
		const last = getLastNonEmptySegment(parsed.pathname)
		return last ? decodeURIComponent(last) : url
	} catch {
		return url
	}
})

const mostrarArchivo = async () => {
	const storagePath = storageRef(storage, pago.value.proof)
	const url = await getDownloadURL(storagePath)
	proofDataUrl.value = url
}
</script>
<template>
	<VaCard class="receipt-card">
		<VaCardContent class="receipt-card-content">
			<div class="receipt-merchant">
				<div class="receipt-merchant-left">
					<div class="receipt-logo" aria-hidden="true">
						<span class="receipt-logo-text">{{ institutionInitial }}</span>
					</div>
					<div class="receipt-merchant-text">
						<div class="receipt-merchant-name">{{ pago.institutionName }} - Pago realizado</div>
						<div class="receipt-merchant-sub">ID: {{ pago.idInstitution }}</div>
					</div>
				</div>
				<div class="receipt-check-inner">
					<VaIcon name="check" class="receipt-check-icon" />
				</div>
			</div>

			<div class="receipt-divider" />

			<div class="receipt-grid">
				<div class="receipt-field">
					<div class="receipt-label">DOCUMENTO</div>
					<div class="receipt-value receipt-value--mono">
						{{ pago.description || '-' }}
					</div>
				</div>

				<div class="receipt-field receipt-field--right">
					<div class="receipt-label">IMPORTE</div>
					<div class="receipt-value receipt-value--amount">
						{{ formatPrice(pago.amount) }}
					</div>
				</div>

				<div class="receipt-field">
					<div class="receipt-label">FECHA DE PAGO</div>
					<div class="receipt-value">
						{{ paymentDateText }}
					</div>
				</div>
				<div class="receipt-actions">
					<VaButton icon="download" color="primary" size="small" class="receipt-download-btn" @click="mostrarArchivo">Enlace al archivo</VaButton>
				</div>
				<div class="receipt-file-container" v-if="proofDataUrl">
					<a :href="proofDataUrl" target="_blank" rel="noopener noreferrer" class="receipt-file-link" aria-label="Abrir comprobante en una nueva pestaña">
						<span class="receipt-file-icon-wrap" aria-hidden="true">
							<VaIcon name="image" class="receipt-file-icon" />
						</span>
						<span class="receipt-file-text">
							<span class="receipt-file-title">Comprobante</span>
							<span class="receipt-file-name" :title="proofDisplayText">{{ proofDisplayText }}</span>
						</span>
						<VaIcon name="open_in_new" class="receipt-file-open" aria-hidden="true" />
					</a>
				</div>
			</div>
		</VaCardContent>
	</VaCard>
</template>

<style scoped>
.receipt-check-inner {
	width: 34px;
	height: 34px;
	border-radius: 999px;
	background: #7cf2a1;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 6px 14px rgba(0, 0, 0, 0.12);
	border: 2px solid #ffffff;
}

.receipt-check-icon {
	color: #ffffff;
	font-size: 16px;
}

.receipt-card {
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 6px 16px rgba(17, 24, 39, 0.1);
}

.receipt-card-content {
	padding: 0.75rem;
}

.receipt-merchant {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	padding: 0;
}

.receipt-merchant-left {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	min-width: 0;
}

.receipt-logo {
	width: 34px;
	height: 34px;
	border-radius: 999px;
	background: #0b1c3a;
	display: grid;
	place-items: center;
	flex: 0 0 auto;
}

.receipt-logo-text {
	color: #ffffff;
	font-weight: 800;
	font-size: 0.9rem;
}

.receipt-merchant-text {
	min-width: 0;
}

.receipt-merchant-name {
	font-size: 1.02rem;
	font-weight: 800;
	color: #2f3a45;
	line-height: 1.2;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.receipt-merchant-sub {
	margin-top: 0.15rem;
	font-size: 0.8rem;
	color: #718096;
}

.receipt-divider {
	height: 1px;
	background: rgba(148, 163, 184, 0.35);
	margin: 0.65rem 0 0.45rem 0;
}

.receipt-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.75rem 0.9rem;
	padding: 0.25rem 0 0 0;
}

.receipt-field--right {
	text-align: right;
}

.receipt-label {
	font-size: 0.72rem;
	letter-spacing: 0.08em;
	font-weight: 700;
	color: #9aa6b2;
}

.receipt-value {
	margin-top: 0.35rem;
	font-size: 0.92rem;
	font-weight: 700;
	color: #2f3a45;
	word-break: break-word;
}

.receipt-value--mono {
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
	font-weight: 650;
	letter-spacing: -0.01em;
	display: -webkit-box;
	line-clamp: 2;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.receipt-value--amount {
	font-size: 1.15rem;
}

.receipt-actions {
	display: flex;
	justify-content: flex-end;
	gap: 0.4rem;
	padding: 0;
	align-items: flex-end;
}

::deep(.receipt-download-btn) {
	min-height: 30px;
	padding: 0 10px;
	font-size: 0.85rem;
}

.receipt-file-container {
	grid-column: 1 / -1;
}

.receipt-file-link {
	display: flex;
	align-items: center;
	gap: 0.65rem;
	padding: 0.65rem 0.75rem;
	border-radius: 12px;
	border: 1px solid rgba(148, 163, 184, 0.35);
	background: #f8fafc;
	text-decoration: none;
	transition:
		background 160ms ease,
		border-color 160ms ease,
		transform 160ms ease;
}

.receipt-file-link:hover {
	background: #eef2ff;
	border-color: rgba(99, 102, 241, 0.35);
	transform: translateY(-1px);
}

.receipt-file-icon-wrap {
	width: 34px;
	height: 34px;
	border-radius: 10px;
	background: #e6f0ff;
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
}

.receipt-file-icon {
	color: #000000;
	font-size: 18px;
}

.receipt-file-text {
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.1rem;
	flex: 1 1 auto;
}

.receipt-file-title {
	font-size: 0.78rem;
	letter-spacing: 0.06em;
	font-weight: 800;
	color: #64748b;
	text-transform: uppercase;
}

.receipt-file-name {
	font-size: 0.9rem;
	font-weight: 750;
	color: #334155;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.receipt-file-open {
	color: #64748b;
	font-size: 18px;
	flex: 0 0 auto;
}

@media (max-width: 640px) {
	.receipt-grid {
		grid-template-columns: 1fr;
	}
	.receipt-field--right {
		text-align: left;
	}
}
</style>
